import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import categoryService from '../../../services/categoryService';
import { useStore } from '../../../context/StoreContext';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import ActiveFilter from '../../common/ActiveFilter';
import { useDataTable } from '../../../hooks/useDataTable';
import { buildToggleAction, buildSellOnlineAction, useCanUpdate } from '../../common/activeToggle';

const CategoriesList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentStore } = useStore();
  const canToggle = useCanUpdate('categories.update');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Page, size, search, sort and the active filter live in the URL; refetches when the store changes.
  const table = useDataTable(categoryService.getCategoriesPage, {}, {
    onError: () => toast.error(t('categories.fetchError')),
    deps: [currentStore],
  });
  const fetchCategories = table.refresh;

  const handleDelete = async () => {
    try {
      await categoryService.deleteCategory(categoryToDelete.id);
      toast.success(t('categories.deleteSuccess'));
      fetchCategories();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.deleteError'));
      console.error(error);
    }
  };

  const handleToggleSellOnline = async (row) => {
    try {
      await categoryService.toggleSellOnline(row.id);
      toast.success(row.sellOnline === false ? t('sellOnline.enabled') : t('sellOnline.disabled'));
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.toggleError'));
    }
  };

  const handleToggleActive = async (row) => {
    try {
      await categoryService.toggleActive(row.id);
      toast.success(row.isActive === false ? t('common.activated') : t('common.deactivated'));
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.toggleError'));
    }
  };

  const columns = [
    {
      field: 'image',
      headerName: '',
      width: 70,
      sortable: false,
      cellRenderer: (params) => (
        params.data.imageUrl ? (
          <Avatar src={params.data.imageUrl} variant="rounded" sx={{ width: 40, height: 40, my: 0.5 }}>
            <CategoryIcon />
          </Avatar>
        ) : (
          <Avatar variant="rounded" sx={{ bgcolor: 'primary.main', width: 40, height: 40, my: 0.5 }}>
            <CategoryIcon />
          </Avatar>
        )
      )
    },
    { field: 'id', headerName: t('common.id'), width: 70 },
    { field: 'name', headerName: t('common.name'), width: 200 },
    { field: 'description', headerName: t('common.description'), flex: 1, minWidth: 200 },
    {
      field: 'parentName',
      headerName: t('categories.parentCategory'),
      width: 150,
      cellRenderer: (params) => (
        params.value ? (
          <Chip label={params.value} size="small" color="info" />
        ) : (
          <Chip label={t('categories_ext.root')} size="small" color="default" />
        )
      )
    },
    {
      field: 'subcategoryCount',
      headerName: t('categories_ext.subcategories'),
      width: 140,
      cellRenderer: (params) => (
        <Chip label={params.value || 0} color="secondary" size="small" />
      )
    },
    {
      field: 'productCount',
      headerName: t('categories.productsCount'),
      width: 120,
      cellRenderer: (params) => (
        <Chip label={params.value || 0} color="primary" size="small" />
      )
    },
    {
      field: 'displayOrder',
      headerName: t('categories_ext.order'),
      width: 90,
    },
    {
      field: 'isActive',
      headerName: t('common.status'),
      width: 120,
      cellRenderer: (params) => (
        <Chip
          label={params.value ? t('common.active') : t('common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'sellOnline',
      headerName: t('sellOnline.column'),
      width: 120,
      sortable: false,
      cellRenderer: (params) => (
        <Chip
          label={params.value === false ? t('sellOnline.offline') : t('sellOnline.online')}
          color={params.value === false ? 'default' : 'info'}
          variant="outlined"
          size="small"
        />
      ),
      tooltipValueGetter: () => null,
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 180,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'view',
              icon: ViewIcon,
              title: t('common.details'),
              color: 'info',
              onClick: () => navigate(`/categories/view/${params.data.id}`),
            },
            {
              key: 'edit',
              icon: EditIcon,
              title: t('common.edit'),
              color: 'primary',
              onClick: () => navigate(`/categories/edit/${params.data.id}`),
            },
            buildToggleAction({ row: params.data, onToggle: handleToggleActive, t, canToggle }),
            buildSellOnlineAction({ row: params.data, onToggle: handleToggleSellOnline, t, canToggle }),
            {
              key: 'delete',
              icon: DeleteIcon,
              title: t('common.delete'),
              color: 'error',
              onClick: () => {
                setCategoryToDelete(params.data);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('categories.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/categories/create')}
        >
          {t('categories.createCategory')}
        </Button>
      </Box>

      <Card sx={{ mb: 2, p: 2 }}>
        <TableSearch
          placeholder={t('common.search')}
          value={table.searchInput}
          onChange={table.handleSearchChange}
        />
      </Card>

      <ActiveFilter
        value={table.params.active || 'all'}
        onChange={(active) => table.updateParams({ active })}
        sx={{ mb: 2 }}
      />

      <Card>
        <AppDataGrid
          rows={table.data.content}
          columns={columns}
          loading={table.loading}
          paginationMode="server"
          rowCount={table.data.totalElements}
          paginationModel={table.paginationModel}
          onPaginationModelChange={table.handlePaginationModelChange}
          sortModel={table.sortModel}
          onSortModelChange={table.handleSortChange}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowId={(row) => row.id}
        />
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('categories.deleteCategory')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common.deleteConfirmText', { name: categoryToDelete?.name })}
            {categoryToDelete?.subcategoryCount > 0 && (
              <strong> {t('categories_ext.hasSubcategoriesWarning', { count: categoryToDelete.subcategoryCount })}</strong>
            )}
            {categoryToDelete?.productCount > 0 && (
              <strong> {t('categories_ext.hasProductsWarning', { count: categoryToDelete.productCount })}</strong>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesList;
